import { createElement, forwardRef } from 'react';

// Web stand-in for the few react-native primitives the shared app source reaches
// for around its drawings: the View a diagram sits in, the StyleSheet its styles
// module is built with, and the Pressable the app's icon module defines (but the
// web never renders). This is deliberately NOT react-native-web: only what the
// shared files use is here, translated narrowly, so a shared file that starts
// using another primitive fails loudly in the web's build and tests instead of
// rendering a guess. Wired in by plugins/appShims.js for importers under app/src.

// React Native numbers are density-independent pixels; on the web they are CSS
// pixels. These properties are the exceptions that take a bare number.
const UNITLESS = new Set([
  'flex',
  'flexGrow',
  'flexShrink',
  'opacity',
  'zIndex',
  'aspectRatio',
  'fontWeight',
]);

// React Native's axis shorthands have no CSS equivalent.
const EXPANSIONS = {
  paddingHorizontal: ['paddingLeft', 'paddingRight'],
  paddingVertical: ['paddingTop', 'paddingBottom'],
  marginHorizontal: ['marginLeft', 'marginRight'],
  marginVertical: ['marginTop', 'marginBottom'],
};

// React Native accepts a style, an array of styles (later wins) or nothing.
export function flattenStyle(style) {
  if (!style) {
    return {};
  }
  if (Array.isArray(style)) {
    return Object.assign({}, ...style.map(flattenStyle));
  }
  return style;
}

// React Native's transform is a list of single-function objects
// ([{ rotate: '180deg' }, { translateX: 4 }]); CSS wants one string, with px on
// the translations.
function transformToCss(transforms) {
  return transforms
    .flatMap((transform) => Object.entries(transform))
    .map(
      ([fn, value]) =>
        `${fn}(${typeof value === 'number' && /^(translate|perspective)/.test(fn) ? `${value}px` : value})`,
    )
    .join(' ');
}

// A React Native style as a React DOM style object.
export function toCss(style) {
  const css = {};
  for (const [key, value] of Object.entries(flattenStyle(style))) {
    if (value === null || value === undefined) {
      continue;
    }
    if (key === 'transform' && Array.isArray(value)) {
      css.transform = transformToCss(value);
      continue;
    }
    for (const cssKey of EXPANSIONS[key] ?? [key]) {
      if (typeof value === 'number') {
        css[cssKey] = UNITLESS.has(cssKey) ? String(value) : `${value}px`;
      } else {
        css[cssKey] = value;
      }
    }
  }
  return css;
}

// React Native accessibility roles the shared files use, as ARIA roles.
const ROLES = {
  image: 'img',
  button: 'button',
  header: 'heading',
  link: 'link',
  none: 'presentation',
};

// A View is a flex column by default (the .rn-view rule in index.css); a role and
// label are the only accessibility props translated.
export const View = forwardRef(function View(
  {
    style,
    accessible,
    accessibilityRole,
    accessibilityLabel,
    accessibilityHint,
    testID,
    children,
    ...rest
  },
  ref,
) {
  return createElement(
    'div',
    {
      ref,
      className: 'rn-view',
      style: toCss(style),
      role: accessibilityRole
        ? (ROLES[accessibilityRole] ?? accessibilityRole)
        : undefined,
      'aria-label': accessibilityLabel,
      'data-testid': testID,
      ...rest,
    },
    children,
  );
});

// Only defined, so the app's icon module (which declares a pressable icon the web
// never renders) can be imported. Enough of a button to be correct if it ever is.
export const Pressable = forwardRef(function Pressable(
  {
    onPress,
    disabled,
    style,
    accessibilityLabel,
    accessibilityRole,
    testID,
    children,
    ...rest
  },
  ref,
) {
  const resolvedStyle =
    typeof style === 'function' ? style({ pressed: false }) : style;
  return createElement(
    'button',
    {
      ref,
      type: 'button',
      onClick: onPress,
      disabled,
      style: toCss(resolvedStyle),
      'aria-label': accessibilityLabel,
      'data-testid': testID,
      ...rest,
    },
    typeof children === 'function' ? children({ pressed: false }) : children,
  );
});

export const StyleSheet = {
  // The app's StyleSheet.create only registers styles; the object is the result.
  create: (styles) => styles,
  flatten: flattenStyle,
  hairlineWidth: 1,
  absoluteFillObject: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
};
StyleSheet.absoluteFill = StyleSheet.absoluteFillObject;

export const Platform = {
  OS: 'web',
  select: (spec) => (spec && 'web' in spec ? spec.web : spec?.default),
};
